/**
 * One-time session/token handoff for the admin portal.
 *
 * The admin is a separate origin (admin.*), so it cannot read the customer
 * app's session storage. The customer app's "Inspect" deep-links therefore
 * carry the current `?token=&refresh_token=` in the hash query. We consume them
 * into this origin's storage and strip them from the URL so a deep-link arrives
 * already authenticated and the secrets never linger in the address bar /
 * browser history / referrer.
 *
 * This MUST run before the router module is evaluated: `createWebHashHistory()`
 * snapshots `window.location` at creation time, so if we stripped the token
 * later the router's first resolved route would still carry it (and pages that
 * `router.replace({ query: { ...route.query } })` would write it straight back
 * into the address bar). Hence this is a side-effect module that `main.js`
 * imports *before* `./router`, and the strip happens here at evaluation time.
 */
export function consumeHandoffTokens() {
  if (typeof window === 'undefined') return;
  const hash = window.location.hash || '';
  const qIndex = hash.indexOf('?');
  if (qIndex === -1) return;

  const params = new URLSearchParams(hash.slice(qIndex + 1));
  const token = params.get('token');
  const refresh = params.get('refresh_token');
  if (!token && !refresh) return;

  if (token) sessionStorage.setItem('accessToken', token);
  if (refresh) localStorage.setItem('refreshToken', refresh);

  params.delete('token');
  params.delete('refresh_token');

  const path = hash.slice(1, qIndex);
  const rest = params.toString();
  const cleanedHash = rest ? `${path}?${rest}` : path;
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${window.location.search}#${cleanedHash}`
  );
}

consumeHandoffTokens();
